import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ScoreRing, PriorityBadge } from "@/components/Score";
import { OpportunityRadar } from "@/components/OpportunityRadar";
import { OutreachStudio } from "@/components/OutreachStudio";
import { PitchSequencer } from "@/components/PitchSequencer";
import { ModeBadge } from "@/components/Brand";
import { moneyRange } from "@/lib/format";
import {
  ArrowLeft, Star, MapPin, Phone, Globe, Bookmark, Loader2, TrendingUp, Ban, Zap,
  Target, ShieldCheck, Sword, Package, Check, Clock, Gauge, Users, ChevronRight, ListOrdered,
  BadgeCheck, AlertTriangle, MinusCircle, Copy, MessageCircle, Navigation,
} from "lucide-react";
import { toast } from "sonner";

const EV_STYLES = {
  FACT: "text-blue-400 bg-blue-500/10 border-blue-500/25",
  INFERENCE: "text-amber-400 bg-amber-500/10 border-amber-500/25",
  ESTIMATE: "text-primary bg-primary/10 border-primary/25",
};

function Bar({ label, value }) {
  const color = value >= 78 ? "#10b981" : value >= 52 ? "#f59e0b" : "#64748b";
  return (
    <div>
      <div className="flex justify-between text-xs mb-1"><span className="text-slate-400">{label}</span><span className="font-mono font-semibold lf-tabnum" style={{ color }}>{value}</span></div>
      <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${value}%`, background: color, transition: "width .8s ease" }} /></div>
    </div>
  );
}

function IntelScore({ label, score, confidence, note }) {
  const nv = score == null;
  return (
    <div className="lf-card p-4 flex items-center gap-4" data-testid={`intel-${label.split(" ")[0].toLowerCase()}`}>
      {nv ? <div className="w-16 h-16 rounded-full border-2 border-dashed border-slate-700 grid place-items-center text-[10px] font-mono text-slate-500">N/V</div> : <ScoreRing score={score} size={64} />}
      <div>
        <p className="text-sm font-semibold text-slate-100">{label}</p>
        <p className="text-[11px] text-slate-500 mt-0.5">{nv ? "Not verified · excluded from scoring" : `${confidence}% confidence`}</p>
        {note && !nv && <p className="text-[10px] text-slate-500">{note}</p>}
      </div>
    </div>
  );
}

export default function OpportunityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const { profile } = useAuth();
  const currency = profile?.currency || "INR";
  const [opp, setOpp] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [card, setCard] = useState(null);
  const [cardLoading, setCardLoading] = useState(false);
  const [offer, setOffer] = useState(null);
  const [offerLoading, setOfferLoading] = useState(false);
  const [tab, setTab] = useState(sp.get("pitch") ? "outreach" : "outreach");

  useEffect(() => { api.get(`/opportunities/${id}`).then(({ data }) => setOpp(data)); }, [id]);

  const save = async () => {
    setSaving(true);
    try { await api.post("/leads", { opportunity_id: id }); setSaved(true); toast.success("Lead saved to pipeline"); }
    catch { toast.error("Could not save lead."); }
    finally { setSaving(false); }
  };

  const genCard = async () => { setCardLoading(true); try { const { data } = await api.post(`/battlecard/${id}`); setCard(data); } catch { toast.error("Failed"); } finally { setCardLoading(false); } };
  const genOffer = async () => { setOfferLoading(true); try { const { data } = await api.post(`/offer/${id}`); setOffer(data); } catch { toast.error("Failed"); } finally { setOfferLoading(false); } };

  if (!opp) return <div className="h-[60vh] grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const low = opp.priority_label === "LOW PRIORITY";
  const rp = opp.provenance?.rating;
  const revVal = opp.provenance?.reviews?.value;
  const ratingProv = rp ? { ...rp, value: rp.value == null ? null : `${rp.value}${revVal != null ? ` · ${revVal} reviews` : ""}` } : null;
  const phoneVal = opp.provenance?.phone?.value;
  const phoneDigits = phoneVal ? phoneVal.replace(/[^\d]/g, "") : "";
  const mapsUrl = (opp.lat != null && opp.lng != null)
    ? `https://www.google.com/maps/dir/?api=1&destination=${opp.lat},${opp.lng}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${opp.name} ${opp.address}`)}`;

  return (
    <div className="space-y-6 max-w-5xl">
      <button onClick={() => navigate(-1)} className="text-sm text-slate-400 hover:text-slate-100 flex items-center gap-1.5" data-testid="back-btn"><ArrowLeft className="w-4 h-4" /> Back</button>

      {/* Salesperson's Lead Card */}
      <div className="lf-card overflow-hidden" data-testid="lead-card">
        <div className="p-6 flex flex-col sm:flex-row sm:items-start gap-6">
          <ScoreRing score={opp.opportunity_score} size={96} label="Opportunity" />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <PriorityBadge label={opp.priority_label} testid="detail-priority" />
              <ModeBadge mode={opp.source === "live" ? "live" : "demo"} testid="detail-source-badge" />
              {opp.source === "live" && <span className="text-[10px] font-mono text-slate-500">Discovered via {opp.provider === "geoapify" ? "Geoapify (OpenStreetMap)" : opp.provider === "google" ? "Google Places" : "Live provider"}{opp.google_type ? ` · ${opp.google_type}` : ""}</span>}
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-50 mt-2">{opp.name}</h1>
            <p className="flex items-center gap-1.5 text-sm text-slate-400 mt-1.5"><MapPin className="w-3.5 h-3.5 shrink-0" />{opp.address}</p>
          </div>
          <Button onClick={save} disabled={saving || saved} className="bg-primary hover:bg-emerald-600 text-white shrink-0" data-testid="save-lead-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <><Check className="w-4 h-4 mr-1.5" />Saved</> : <><Bookmark className="w-4 h-4 mr-1.5" />Save lead</>}
          </Button>
        </div>

        <VerificationBanner opp={opp} />

        {/* Contact facts with field-level provenance */}
        <div className="grid sm:grid-cols-2 gap-px bg-slate-800 border-t border-slate-800">
          <ContactItem icon={Phone} label="Phone" prov={opp.provenance?.phone} testid="lead-phone" />
          <ContactItem icon={Star} label="Rating" prov={ratingProv} testid="lead-rating" />
          <ContactItem icon={Globe} label="Website" prov={opp.provenance?.website} isLink testid="lead-website" />
          <ContactItem icon={Clock} label="Hours" prov={opp.provenance?.hours} testid="lead-hours" />
        </div>

        {/* Quick actions for the salesperson */}
        <div className="flex flex-wrap gap-2 p-4 border-t border-slate-800 bg-slate-900/40" data-testid="lead-actions">
          <ActionBtn href={phoneDigits ? `tel:${phoneDigits}` : null} icon={Phone} label="Call" testid="action-call" />
          <ActionBtn href={phoneDigits ? `https://wa.me/${phoneDigits}` : null} external icon={MessageCircle} label="WhatsApp" testid="action-whatsapp" />
          <ActionBtn onClick={phoneVal ? () => { navigator.clipboard?.writeText(phoneVal); toast.success("Phone copied"); } : null} icon={Copy} label="Copy phone" testid="action-copy-phone" />
          <ActionBtn href={opp.website || null} external icon={Globe} label="Website" testid="action-website" />
          <ActionBtn href={mapsUrl} external icon={Navigation} label="Directions" testid="action-directions" />
        </div>
      </div>

      {/* AI Opportunity Brief — the WOW card */}
      <div className="lf-card lf-glow overflow-hidden" data-testid="opportunity-brief">
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-primary/5">
          <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-primary" /><span className="font-mono text-xs uppercase tracking-widest text-primary">AI Opportunity Brief</span></div>
          <span className="font-mono text-sm font-bold text-slate-200">{opp.opportunity_score}/100</span>
        </div>
        <div className="p-6 space-y-5">
          <Section icon={Target} title="The opportunity">{opp.why_service}</Section>
          <div>
            <SecTitle icon={ShieldCheck} title="The evidence" />
            <ul className="mt-2 space-y-1.5">
              {opp.evidence.map((e, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <span className={`text-[9px] font-mono font-bold px-1.5 py-0.5 rounded border mt-0.5 shrink-0 ${EV_STYLES[e.type]}`}>{e.type}</span>
                  <span className="text-slate-300">{e.text}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <Section icon={TrendingUp} title="What to sell">
              <span className="text-slate-100 font-semibold">{opp.primary_service}</span>
              {opp.secondary_service && <span className="text-slate-400"> + {opp.secondary_service}</span>}
            </Section>
            <Section icon={Ban} title="Don't pitch"><span className="text-slate-300 font-semibold">{opp.do_not_pitch}</span></Section>
            <Section icon={Zap} title="Estimated value"><span className="text-emerald-400 font-semibold">{moneyRange(opp.estimated_value_min, opp.estimated_value_max, opp.currency || currency)}</span></Section>
            <Section icon={Clock} title="Why now">{opp.why_now}</Section>
          </div>
          <div className="rounded-xl bg-primary/5 border border-primary/20 p-4">
            <p className="text-[10px] font-mono uppercase tracking-wider text-primary mb-1">Best sales angle</p>
            <p className="font-display text-lg text-slate-100">"{opp.sales_angle}"</p>
          </div>
          {low && (
            <div className="rounded-xl bg-slate-800/40 border border-slate-700 p-4">
              <p className="text-sm font-semibold text-slate-200 mb-1">Don't waste your time here.</p>
              <p className="text-sm text-slate-400">{opp.low_reasons.join(" · ")}. Focus your energy on higher-opportunity prospects.</p>
            </div>
          )}
        </div>
      </div>

      {/* Radar + score breakdown */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="lf-card p-5">
          <h3 className="font-display font-semibold text-slate-100 mb-2 flex items-center gap-2"><Gauge className="w-4 h-4 text-primary" /> Opportunity Radar</h3>
          <OpportunityRadar radar={opp.radar} />
        </div>
        <div className="lf-card p-5 space-y-4">
          <div>
            <h3 className="font-display font-semibold text-slate-100 mb-3">Why this score?</h3>
            <div className="space-y-2">
              {opp.score_breakdown.map((c) => (
                <div key={c.key} className="flex items-center gap-3">
                  <span className="font-mono text-xs font-bold text-primary w-8 lf-tabnum">+{c.points}</span>
                  <span className="text-sm text-slate-300 flex-1">{c.label}</span>
                  <div className="w-24 h-1.5 rounded-full bg-slate-800 overflow-hidden"><div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(c.points * 3, 100)}%` }} /></div>
                </div>
              ))}
            </div>
          </div>
          <div className="pt-3 border-t border-slate-800 grid grid-cols-3 gap-3 text-center">
            <div><p className="text-[10px] font-mono uppercase text-slate-500">Opportunity</p><p className="font-display text-xl font-bold text-primary lf-tabnum">{opp.opportunity_score}</p></div>
            <div><p className="text-[10px] font-mono uppercase text-slate-500">Readiness</p><p className="font-display text-xl font-bold text-amber-400 lf-tabnum">{opp.buying_readiness}</p></div>
            <div><p className="text-[10px] font-mono uppercase text-slate-500">Priority</p><p className="font-display text-xl font-bold text-slate-100 lf-tabnum">{opp.priority_score}</p></div>
          </div>
          <p className="text-[11px] text-slate-500">Buying readiness is an estimate from observable signals — not a prediction of behaviour.</p>
        </div>
      </div>

      {/* Intelligence scores */}
      <div className="grid sm:grid-cols-3 gap-4">
        <IntelScore label="Website Intelligence" score={opp.website_score} confidence={opp.confidence.website} />
        <IntelScore label="Reputation Intelligence" score={opp.reputation_score} confidence={opp.confidence.google} />
        <IntelScore label="Social Intelligence" score={opp.social_score} confidence={opp.confidence.social} note={opp.signals?.social_source} />
      </div>
      <div className="lf-card p-5">
        <p className="text-sm text-slate-300"><span className="font-mono text-xs text-primary">WEBSITE {opp.website_score == null ? "N/V" : `${opp.website_score}/100`} · </span>{opp.website_summary}</p>
        {opp.source === "live" && opp.signals?.website_analyzed && (
          <div className="mt-3" data-testid="website-facts">
            <p className="text-[10px] font-mono uppercase tracking-wider text-blue-400 mb-1.5 flex items-center gap-1.5">
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded border text-blue-400 bg-blue-500/10 border-blue-500/25">FACT</span>
              Live website check · fetched just now
            </p>
            <div className="flex flex-wrap gap-1.5">
              {[["HTTPS", opp.signals.https], ["Mobile viewport", opp.signals.mobile_friendly], ["Booking", opp.signals.has_booking], ["WhatsApp link", opp.signals.has_whatsapp], ["Contact form", opp.signals.has_lead_form], ["Chatbot", opp.signals.has_chatbot], ["Testimonials", opp.signals.testimonials]].map(([k, v]) => (
                <span key={k} className={`text-[10px] font-mono px-2 py-0.5 rounded border ${v ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/25" : "text-slate-500 bg-slate-800/40 border-slate-700"}`}>{k}: {v ? "detected" : "not detected"}</span>
              ))}
            </div>
          </div>
        )}
        {opp.source === "live" && opp.signals?.website_exists && opp.signals?.website_analyzed === false && (
          <p className="mt-3 text-[11px] text-amber-400/80" data-testid="website-unfetched">
            Website is listed but could not be fetched ({opp.signals.website_fetch_error || "fetch failed"}) — website signals are excluded from scoring, not counted against the business.
          </p>
        )}
        {(opp.data_notes || []).length > 0 && (
          <ul className="mt-3 space-y-0.5" data-testid="data-notes">{opp.data_notes.map((n, i) => <li key={i} className="text-[11px] text-slate-500">· {n}</li>)}</ul>
        )}
        <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-[11px] text-slate-500 font-mono">
          <span>Listing: {opp.freshness.google}</span><span>Website: {opp.freshness.website}</span><span>Analysis: {opp.freshness.analysis}</span><span>Overall confidence: {opp.confidence.overall}%</span>
        </div>
      </div>

      {/* Competitive intelligence */}
      <div className="lf-card p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-semibold text-slate-100 flex items-center gap-2"><Users className="w-4 h-4 text-primary" /> Competitive Intelligence</h3>
          <PriorityBadge label={`GAP: ${opp.competitive_gap_label}`} />
        </div>
        {opp.competitor ? (
          <>
            <div className="grid sm:grid-cols-2 gap-4">
              <CompCard title="Target" name={opp.name} rating={opp.rating} reviews={opp.reviews} website={!!opp.website} booking={opp.signals.has_booking} highlight />
              <CompCard title="Benchmark competitor" name={opp.competitor.name} rating={opp.competitor.rating} reviews={opp.competitor.reviews} website={opp.competitor.has_website} booking={opp.competitor.has_booking} />
            </div>
            <p className="text-[11px] text-slate-500 mt-3" data-testid="benchmark-note">{opp.competitor.source || "Local benchmark for comparison."} Only the benchmark shown is compared — no broader competitor claims.</p>
          </>
        ) : (
          <p className="text-sm text-slate-500" data-testid="no-benchmark">No comparable same-category business found in the results — competitive gap not verified and excluded from scoring.</p>
        )}
      </div>

      {/* Playbook */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="outreach" data-testid="tab-outreach">Human Outreach</TabsTrigger>
          <TabsTrigger value="sequence" data-testid="tab-sequence"><ListOrdered className="w-3.5 h-3.5 mr-1.5" />Pitch Sequencer</TabsTrigger>
          <TabsTrigger value="battlecard" data-testid="tab-battlecard">Sales Battlecard</TabsTrigger>
          <TabsTrigger value="offer" data-testid="tab-offer">Offer Builder</TabsTrigger>
        </TabsList>
        <TabsContent value="outreach" className="mt-4">
          <div className="lf-card p-5"><OutreachStudio oppId={id} /></div>
        </TabsContent>
        <TabsContent value="sequence" className="mt-4">
          <div className="lf-card p-5"><PitchSequencer oppId={id} /></div>
        </TabsContent>
        <TabsContent value="battlecard" className="mt-4">
          <div className="lf-card p-5">
            {!card ? (
              <div className="text-center py-8">
                <Sword className="w-6 h-6 text-primary mx-auto mb-2" />
                <p className="text-sm text-slate-400 mb-4">Generate a full sales battlecard for {opp.name}.</p>
                <Button onClick={genCard} disabled={cardLoading} className="bg-primary hover:bg-emerald-600 text-white" data-testid="generate-battlecard-btn">
                  {cardLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generate battlecard"}
                </Button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {[["Opening", card.opening], ["Problem", card.problem], ["Evidence", card.evidence], ["Recommended offer", card.offer], ["Best angle", card.angle], ["Likely objection", card.objection], ["Suggested response", card.response], ["Follow-up", card.follow_up], ["Second follow-up", card.second_follow_up]].map(([k, v]) => (
                  <div key={k} className="rounded-lg bg-slate-900/60 border border-slate-800 p-3">
                    <p className="text-[10px] font-mono uppercase tracking-wider text-primary mb-1">{k}</p>
                    <p className="text-sm text-slate-300">{v}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
        <TabsContent value="offer" className="mt-4">
          <div className="lf-card p-5">
            {!offer ? (
              <div className="text-center py-8">
                <Package className="w-6 h-6 text-primary mx-auto mb-2" />
                <p className="text-sm text-slate-400 mb-4">Build a productised offer around {opp.primary_service}.</p>
                <Button onClick={genOffer} disabled={offerLoading} className="bg-primary hover:bg-emerald-600 text-white" data-testid="generate-offer-btn">
                  {offerLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Build offer"}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <h4 className="font-display text-lg font-semibold text-slate-100">{offer.name}</h4>
                <p className="text-sm text-slate-400">{offer.scope}</p>
                <div><p className="text-[10px] font-mono uppercase text-primary mb-1.5">Deliverables</p><ul className="space-y-1">{(offer.deliverables || []).map((d, i) => <li key={i} className="text-sm text-slate-300 flex items-center gap-2"><ChevronRight className="w-3.5 h-3.5 text-primary" />{d}</li>)}</ul></div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><p className="text-[10px] font-mono uppercase text-primary mb-1">Suggested price</p><p className="text-sm font-semibold text-emerald-400">{offer.price_range}</p></div>
                  <div><p className="text-[10px] font-mono uppercase text-primary mb-1">CTA</p><p className="text-sm text-slate-300">{offer.cta}</p></div>
                </div>
                <div className="rounded-lg bg-primary/5 border border-primary/20 p-3"><p className="text-[10px] font-mono uppercase text-primary mb-1">Value proposition</p><p className="text-sm text-slate-200">{offer.value_prop}</p></div>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SecTitle({ icon: Icon, title }) {
  return <p className="text-[10px] font-mono uppercase tracking-wider text-primary flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" />{title}</p>;
}
function Section({ icon, title, children }) {
  return <div><SecTitle icon={icon} title={title} /><p className="text-sm text-slate-300 mt-1.5 leading-relaxed">{children}</p></div>;
}
function CompCard({ title, name, rating, reviews, website, booking, highlight }) {
  return (
    <div className={`rounded-xl p-4 border ${highlight ? "bg-primary/5 border-primary/25" : "bg-slate-900/60 border-slate-800"}`}>
      <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">{title}</p>
      <p className="font-display font-semibold text-slate-100 mb-3">{name}</p>
      <div className="space-y-2 text-sm">
        <Row label="Rating" value={`${rating}★`} />
        <Row label="Reviews" value={reviews} />
        <Row label="Website" value={website == null ? "Not verified" : website ? "Yes" : "No"} ok={website == null ? undefined : website} />
        <Row label="Booking" value={booking == null ? "Not verified" : booking ? "Yes" : "No"} ok={booking == null ? undefined : booking} />
      </div>
    </div>
  );
}
function Row({ label, value, ok }) {
  return <div className="flex justify-between"><span className="text-slate-500">{label}</span><span className={`font-medium ${ok === false ? "text-slate-500" : ok === true ? "text-primary" : "text-slate-200"}`}>{value}</span></div>;
}

const PROV_STYLES = {
  verified: { cls: "text-emerald-400 bg-emerald-500/10 border-emerald-500/25", Icon: BadgeCheck },
  unverified: { cls: "text-amber-400 bg-amber-500/10 border-amber-500/25", Icon: AlertTriangle },
  unavailable: { cls: "text-slate-500 bg-slate-800/40 border-slate-700", Icon: MinusCircle },
  demo: { cls: "text-blue-400 bg-blue-500/10 border-blue-500/25", Icon: ShieldCheck },
};

function ProvChip({ state, source }) {
  const cfg = PROV_STYLES[state] || PROV_STYLES.unavailable;
  const { Icon } = cfg;
  const text = state === "verified" ? `Verified · ${source}`
    : state === "unverified" ? `Not verified · ${source}`
    : state === "demo" ? source
    : "Not available";
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border ${cfg.cls}`} data-testid={`prov-${state}`}>
      <Icon className="w-2.5 h-2.5" />{text}
    </span>
  );
}

function ContactItem({ icon: Icon, label, prov, isLink, testid }) {
  const state = prov?.state || "unavailable";
  const value = prov?.value;
  return (
    <div className="bg-slate-950/40 p-4" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-slate-500"><Icon className="w-3 h-3" />{label}</span>
        <ProvChip state={state} source={prov?.source} />
      </div>
      {value == null ? (
        <p className="text-sm text-slate-600 italic">Not available</p>
      ) : isLink ? (
        <a href={value} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline break-all">{value}</a>
      ) : (
        <p className="text-sm font-medium text-slate-100 break-words">{value}</p>
      )}
    </div>
  );
}

function VerificationBanner({ opp }) {
  if (opp.source !== "live") return null;
  const e = opp.enrichment || {};
  let tone = "neutral", Icon = ShieldCheck, text;
  if (opp.provider === "google") {
    tone = "ok"; Icon = BadgeCheck;
    text = "Business facts discovered directly from Google Places — verified at source.";
  } else if (e.status === "verified") {
    tone = "ok"; Icon = BadgeCheck;
    text = `Verified against Google Places — matched "${e.matched_name}" ${e.distance_m}m from the discovered location.`;
  } else if (e.status === "no_match") {
    tone = "warn"; Icon = AlertTriangle;
    text = "No strict Google Places match found — showing the discovery-source listing (Not verified). We won't attach data we can't confirm.";
  } else if (e.status === "unavailable") {
    tone = "warn"; Icon = AlertTriangle;
    text = "Google verification is temporarily unavailable (rate limit) — showing the discovery-source listing (Not verified).";
  } else {
    tone = "neutral"; Icon = Clock;
    text = "Verification pending — opening this lead fetches verified Google Places facts.";
  }
  const cls = tone === "ok" ? "text-emerald-300 bg-emerald-500/5 border-emerald-500/20"
    : tone === "warn" ? "text-amber-300 bg-amber-500/5 border-amber-500/20"
    : "text-slate-400 bg-slate-800/30 border-slate-700";
  return (
    <div className={`flex items-start gap-2 px-6 py-3 border-t text-xs ${cls}`} data-testid="verification-banner">
      <Icon className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span>{text}</span>
    </div>
  );
}

function ActionBtn({ href, onClick, external, icon: Icon, label, testid }) {
  const disabled = !href && !onClick;
  const base = "inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg border transition-colors";
  const active = "border-slate-700 text-slate-200 hover:bg-slate-800 hover:border-slate-600";
  const off = "border-slate-800 text-slate-600 cursor-not-allowed opacity-60";
  if (disabled) return <span className={`${base} ${off}`} data-testid={testid}><Icon className="w-3.5 h-3.5" />{label}</span>;
  if (href) return <a href={href} {...(external ? { target: "_blank", rel: "noreferrer" } : {})} className={`${base} ${active}`} data-testid={testid}><Icon className="w-3.5 h-3.5" />{label}</a>;
  return <button onClick={onClick} className={`${base} ${active}`} data-testid={testid}><Icon className="w-3.5 h-3.5" />{label}</button>;
}
